import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import {
  SportsBasketball,
  Star,
  Schedule,
  LocationOn,
  Add,
  CalendarToday
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { ScheduleGame } from '../types';
import { scheduleAPI } from '../services/api';
import { getSpreadDisplay } from '../utils/gameUtils';

interface BasketballDayPickerProps {
  startDate: Date;
  selectedGames: ScheduleGame[];
  onGameToggle: (game: ScheduleGame) => void;
  maxGames: number;
  showTop25Only?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

export const BasketballDayPicker: React.FC<BasketballDayPickerProps> = ({
  startDate,
  selectedGames,
  onGameToggle,
  maxGames,
  showTop25Only = false
}) => {
  const [dayIndex, setDayIndex] = useState(0);
  const [gamesPerDay, setGamesPerDay] = useState<{ [key: string]: ScheduleGame[] }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Generate array of 7 days starting from startDate
  const days = Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return date;
  });

  // Load games for current day when day changes
  useEffect(() => {
    loadGamesForDay(days[dayIndex]);
  }, [dayIndex]); // Only reload when day changes, not when filter changes

  const loadGamesForDay = async (date: Date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');

    // Check if already loaded
    if (gamesPerDay[dateStr]) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const games = await scheduleAPI.getGamesByDate('basketball', dateStr);

      // Store all games (don't filter here - we'll filter in the render)
      setGamesPerDay(prev => ({
        ...prev,
        [dateStr]: games
      }));
    } catch (err: any) {
      console.error('Error loading games:', err);
      setError(err.response?.data?.error || 'Failed to load games');
    } finally {
      setLoading(false);
    }
  };

  const formatTeamWithRanking = (teamName: string, ranking?: number) => {
    if (ranking) {
      return `#${ranking} ${teamName}`;
    }
    return teamName;
  };

  const formatDateTime = (dateString: string) => {
    return dayjs(dateString).format('ddd, MMM D @ h:mm A');
  };

  const getGamePriorityColor = (priority: number) => {
    if (priority >= 100) return 'error';
    if (priority >= 50) return 'warning';
    return 'default';
  };

  const getCurrentDayGames = () => {
    const dateStr = dayjs(days[dayIndex]).format('YYYY-MM-DD');
    const allGames = gamesPerDay[dateStr] || [];

    // Apply top 25 filter if enabled
    return showTop25Only
      ? allGames.filter((game: ScheduleGame) => game.is_top25)
      : allGames;
  };

  const getGameCount = (date: Date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');
    const allGames = gamesPerDay[dateStr] || [];

    // Apply top 25 filter if enabled for game count
    const filteredGames = showTop25Only
      ? allGames.filter((game: ScheduleGame) => game.is_top25)
      : allGames;

    return filteredGames.length;
  };

  const availableGames = getCurrentDayGames().filter(
    game => !selectedGames.some(selected => selected.id === game.id)
  );

  return (
    <Box>
      {/* Day Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={dayIndex}
          onChange={(e, newValue) => setDayIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {days.map((date, index) => {
            const dateStr = dayjs(date).format('YYYY-MM-DD');
            const gameCount = getGameCount(date);
            const hasLoadedGames = gamesPerDay[dateStr] !== undefined;

            return (
              <Tab
                key={index}
                icon={<CalendarToday />}
                iconPosition="start"
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {dayjs(date).format('ddd, MMM D')}
                    </Typography>
                    {hasLoadedGames && (
                      <Typography variant="caption" color="textSecondary">
                        {gameCount} games
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ minWidth: 120 }}
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {/* Games for Selected Day */}
      {!loading && (
        <TabPanel value={dayIndex} index={dayIndex}>
          {availableGames.length === 0 ? (
            <Typography variant="body1" color="textSecondary" align="center" sx={{ py: 4 }}>
              {getCurrentDayGames().length === 0
                ? 'No games scheduled for this day'
                : 'All games for this day have been selected'}
            </Typography>
          ) : (
            <Box sx={{ display: 'grid', gap: 2 }}>
              {availableGames.map((game) => (
                <Card
                  key={game.id}
                  variant="outlined"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                    opacity: selectedGames.length >= maxGames ? 0.5 : 1
                  }}
                  onClick={() => onGameToggle(game)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <SportsBasketball sx={{ mr: 1 }} />
                          <Typography variant="h6">
                            {formatTeamWithRanking(game.away_team, game.away_ranking)} @ {formatTeamWithRanking(game.home_team, game.home_ranking)}
                          </Typography>

                          {game.is_top25 && (
                            <Star sx={{ ml: 1, color: 'gold' }} />
                          )}

                          <Chip
                            size="small"
                            label={`Priority: ${game.priority}`}
                            color={getGamePriorityColor(game.priority)}
                            sx={{ ml: 1 }}
                          />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Schedule sx={{ mr: 0.5, fontSize: 16 }} />
                            <Typography variant="body2" color="textSecondary">
                              {formatDateTime(game.start_date)}
                            </Typography>
                          </Box>

                          {game.venue && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <LocationOn sx={{ mr: 0.5, fontSize: 16 }} />
                              <Typography variant="body2" color="textSecondary">
                                {game.venue}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {game.conference_game && (
                            <Chip size="small" label="Conference" variant="outlined" />
                          )}
                          {game.neutral_site && (
                            <Chip size="small" label="Neutral Site" variant="outlined" />
                          )}
                          {game.spread ? (
                            <Chip
                              label={getSpreadDisplay(game)}
                              size="small"
                              color="info"
                              variant="outlined"
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                bgcolor: 'info.light',
                                color: 'info.contrastText'
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="No spread"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </Box>

                      <IconButton
                        color="primary"
                        disabled={selectedGames.length >= maxGames}
                      >
                        <Add />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </TabPanel>
      )}
    </Box>
  );
};
